import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, IconButton, Breadcrumbs, Link,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { axiosPartners } from "../../axios/axiosConfig";
import toast from "react-hot-toast";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const consentColor = (status) => {
  const s = status?.toUpperCase() || "";
  if (s === "ENABLED")   return { bg: "#dcfce7", color: "#16a34a" };
  if (s === "CANCELLED") return { bg: "#fee2e2", color: "#dc2626" };
  if (s === "DISABLED")  return { bg: "#fef9c3", color: "#b45309" };
  return { bg: "#f1f5f9", color: "#64748b" };
};

const userTypeColor = (type) => {
  const t = type?.toLowerCase() || "";
  if (t === "admin")   return { bg: "#fae8ff", color: "#9333ea" };
  if (t === "advisor") return { bg: "#eff6ff", color: "#2563eb" };
  if (t === "agent")   return { bg: "#fff7ed", color: "#ea580c" };
  return { bg: "#f1f5f9", color: "#64748b" };
};

const formatDate = (val) => {
  if (!val) return "—";
  try {
    return new Date(val).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return val; }
};

const SymbolBrokerUserList = () => {
  const { symbolName, brokerName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const decodedSymbol = decodeURIComponent(symbolName);
  const decodedBroker = decodeURIComponent(brokerName);

  const symbolId  = location.state?.symbolId;
  const brokerId  = location.state?.brokerId;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosPartners.get(
        `/brokers/symbolwise-broker-user-list/?symbol_id=${symbolId}&broker_id=${brokerId}`
      );
      setUsers(res.data?.response ?? []);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [symbolId, brokerId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUserClick = (user) => {
    navigate(`../user/${user.id}/trades`, {
      relative: "path",
      state: {
        symbolId,
        brokerId,
        clientId: user.id,
        userName: user.username,
        symbolName: decodedSymbol,
        brokerName: decodedBroker,
      },
    });
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>

      {/* Header */}
      <Box sx={{ background: "#04343a", borderRadius: "12px", px: 3, py: 2, mb: 2, display: "flex", alignItems: "center", gap: 1.5, boxShadow: "0 4px 20px rgba(4,52,58,0.3)" }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: "#eef2f7", p: 0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <PeopleIcon sx={{ color: "#eef2f7", fontSize: 26 }} />
        <Box>
          <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
            {decodedBroker} — Users
          </Typography>
          <Typography sx={{ color: "#b8d4c4", fontSize: 12 }}>
            {decodedSymbol} · {users.length} user{users.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
      </Box>

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component="button" underline="hover" sx={{ fontSize: 12, color: "#016551", cursor: "pointer" }} onClick={() => navigate(-2)}>
          Symbolwise Dashboard
        </Link>
        <Link component="button" underline="hover" sx={{ fontSize: 12, color: "#016551", cursor: "pointer" }} onClick={() => navigate(-1)}>
          {decodedSymbol}
        </Link>
        <Typography sx={{ fontSize: 12, color: "#0f172a", fontWeight: 600 }}>{decodedBroker}</Typography>
      </Breadcrumbs>

      {/* Table */}
      <Box sx={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <Box sx={{ background: "#04343a", px: 3, py: 1.5 }}>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>
            {users.length} User{users.length !== 1 ? "s" : ""} · {decodedBroker} · {decodedSymbol}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#016551" }} size={36} />
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>No users found</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["#", "Name", "Contact", "Type", "Consent", "Joined", "Expires", "Last Login"].map((h) => (
                    <TableCell key={h} sx={{ background: "#04343a", color: "#fff", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", py: 1.5, whiteSpace: "nowrap" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user, i) => {
                  const tc = userTypeColor(user.user_type);
                  const cc = consentColor(user.consent_status);
                  return (
                    <TableRow key={user.id} onClick={() => handleUserClick(user)} sx={{ cursor: "pointer", "&:hover": { background: "#f0fdf4" }, transition: "background 0.15s" }}>
                      <TableCell sx={{ fontSize: 12, color: "#94a3b8", py: 1.5, width: 40 }}>{i + 1}</TableCell>

                      <TableCell sx={{ py: 1.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#04343a", lineHeight: 1.3 }}>
                          {user.username}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace" }}>
                          {user.userid}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ py: 1.5 }}>
                        <Typography sx={{ fontSize: 12, color: "#0f172a" }}>{user.email}</Typography>
                        <Typography sx={{ fontSize: 11, color: "#64748b" }}>{user.phoneno}</Typography>
                      </TableCell>

                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          label={user.user_type || "—"}
                          size="small"
                          sx={{ background: tc.bg, color: tc.color, fontWeight: 700, fontSize: 11, height: 22, borderRadius: "6px" }}
                        />
                      </TableCell>

                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          label={user.consent_status || "—"}
                          size="small"
                          sx={{ background: cc.bg, color: cc.color, fontWeight: 700, fontSize: 11, height: 22, borderRadius: "6px" }}
                        />
                      </TableCell>

                      <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>
                        <Typography sx={{ fontSize: 12, color: "#0f172a" }}>{formatDate(user.date_joined)}</Typography>
                      </TableCell>

                      <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>
                        <Typography sx={{ fontSize: 12, color: user.expired_date && new Date(user.expired_date) < new Date() ? "#dc2626" : "#0f172a" }}>
                          {formatDate(user.expired_date)}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>
                        <Typography sx={{ fontSize: 12, color: "#64748b" }}>{formatDate(user.last_login)}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default SymbolBrokerUserList;
